import { useState, useCallback } from "react";
import { supabase } from "@/lib/supabase";

interface NewsletterBoxProps {
  variant?: "dark" | "light" | "card" | "inline";
  title?: string;
  description?: string;
  className?: string;
}

export default function NewsletterBox({
  variant = "dark",
  title = "Bleiben Sie informiert.",
  description = "Erhalten Sie die wichtigsten Nachrichten aus der Schweiz direkt in Ihr Postfach.",
  className = "",
}: NewsletterBoxProps) {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; consent?: string }>({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const validate = useCallback(() => {
    const newErrors: { email?: string; consent?: string } = {};
    if (!email.trim()) {
      newErrors.email = "Bitte geben Sie Ihre E-Mail-Adresse ein.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = "Bitte geben Sie eine gültige E-Mail-Adresse ein.";
    }
    if (!consent) {
      newErrors.consent = "Bitte stimmen Sie der Datenschutzerklärung zu.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [email, consent]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!validate()) return;
      setSubmitting(true);
      setErrors({});

      try {
        // Duplicate email kontrolu
        const { data: existing } = await supabase
          .from("newsletter_subscribers")
          .select("id")
          .eq("email", email)
          .maybeSingle();

        if (existing) {
          setErrors({ email: "Diese E-Mail-Adresse ist bereits für den Newsletter angemeldet." });
          setSubmitting(false);
          return;
        }

        // Supabase'e kaydet
        const { error } = await supabase
          .from("newsletter_subscribers")
          .insert({ email, is_active: true, source: "website" });

        if (error) {
          console.error("[Newsletter] Supabase insert error:", error.message, error.code, error.details);
          setErrors({ email: "Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut." });
          setSubmitting(false);
          return;
        }

        // Basarili!
        setSubmitted(true);
        setEmail("");
        setConsent(false);
      } catch (err) {
        console.error("[Newsletter] Unexpected error:", err);
        setErrors({ email: "Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut." });
      } finally {
        setSubmitting(false);
      }
    },
    [validate, email]
  );

  const handleReset = useCallback(() => {
    setSubmitted(false);
    setEmail("");
    setConsent(false);
    setErrors({});
  }, []);

  const isDark = variant === "dark" || variant === "card";
  const isCard = variant === "card";
  const isInline = variant === "inline";

  const containerClasses = isCard
    ? "bg-white rounded-2xl border border-hj-gray-border p-6 md:p-8 lg:p-10"
    : isInline
    ? ""
    : isDark
    ? "bg-hj-dark text-white"
    : "bg-hj-gray-light text-hj-dark";

  const textColor = isDark && !isCard ? "text-white" : "text-hj-dark";
  const mutedColor = isDark && !isCard ? "text-white/70" : "text-hj-gray";
  const inputBg = isDark && !isCard ? "bg-white/10 border-white/20 placeholder-white/50" : "bg-hj-gray-light border-hj-gray-border placeholder-hj-muted";
  const inputText = isDark && !isCard ? "text-white" : "text-hj-dark";
  const inputFocus = isDark && !isCard ? "focus:ring-white/30" : "focus:ring-hj-red/20";
  const checkboxBorder = isDark && !isCard ? "border-white/30" : "border-hj-gray-border";
  const checkboxBg = isDark && !isCard ? "bg-white/10" : "bg-white";
  const errorColor = "text-red-500";
  const linkColor = isDark && !isCard ? "text-white/80 hover:text-white" : "text-hj-red hover:text-hj-red-light";

  if (submitted) {
    return (
      <div className={`${containerClasses} ${className}`}>
        <div className="flex flex-col sm:flex-row items-start gap-4">
          <div className="w-12 h-12 rounded-full bg-emerald-50 border border-emerald-100 flex items-center justify-center shrink-0">
            <i className="ri-check-line text-emerald-600 text-xl"></i>
          </div>
          <div>
            <h3 className={`text-lg md:text-xl font-bold ${textColor} font-heading`}>
              Vielen Dank!
            </h3>
            <p className={`text-sm ${mutedColor} mt-1.5 leading-relaxed font-body max-w-lg`}>
              Sie haben sich erfolgreich für unseren Newsletter angemeldet. Prüfen Sie Ihr Postfach für die Bestätigungs-E-Mail.
            </p>
            <button
              onClick={handleReset}
              className={`mt-4 text-sm font-medium ${linkColor} transition-colors font-body`}
            >
              Neue Anmeldung
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${containerClasses} ${className}`}>
      <form
        onSubmit={handleSubmit}
        className={isInline ? "flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6 w-full" : ""}
      >
        {/* Icon + Text */}
        <div className={isInline ? "flex items-start gap-3 md:gap-4 flex-1" : "flex flex-col sm:flex-row items-start gap-4 md:gap-5"}>
          {!isInline && (
            <div className={`w-12 h-12 md:w-14 md:h-14 rounded-full ${isDark && !isCard ? "bg-white/10" : "bg-hj-red/10 border border-hj-red/10"} flex items-center justify-center shrink-0`}>
              <i className={`ri-mail-send-line text-xl md:text-2xl ${isDark && !isCard ? "text-white" : "text-hj-red"}`}></i>
            </div>
          )}
          <div className={isInline ? "flex-1" : "flex-1 min-w-0"}>
            <h3 className={`text-lg md:text-xl lg:text-2xl font-bold ${textColor} font-heading leading-tight`}>{title}</h3>
            <p className={`text-sm md:text-base ${mutedColor} mt-2 leading-relaxed font-body max-w-xl`}>{description}</p>
          </div>
        </div>

        {/* Form fields */}
        <div className={isInline ? "flex flex-col sm:flex-row gap-2 w-full md:w-auto" : "mt-5 md:mt-6"}>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 min-w-0">
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (errors.email) setErrors((prev) => ({ ...prev, email: undefined }));
                }}
                placeholder="Ihre E-Mail-Adresse"
                required
                className={`w-full px-4 py-3 border rounded-lg text-sm ${inputBg} ${inputText} focus:outline-none focus:ring-2 ${inputFocus} focus:border-transparent`}
              />
              {errors.email && (
                <p className={`text-xs ${errorColor} mt-1.5 font-body`}>{errors.email}</p>
              )}
            </div>
            <button
              type="submit"
              disabled={submitting}
              className={`px-6 py-3 text-sm font-semibold rounded-lg transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2 ${
                isDark && !isCard
                  ? "bg-white text-hj-dark hover:bg-white/90"
                  : "bg-hj-red text-white hover:bg-hj-red-light"
              }`}
            >
              {submitting ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  Wird gesendet...
                </>
              ) : (
                <>
                  <i className="ri-mail-line"></i>
                  Abonnieren
                </>
              )}
            </button>
          </div>

          {/* Consent */}
          <div className="mt-3">
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                name="consent"
                checked={consent}
                onChange={(e) => {
                  setConsent(e.target.checked);
                  if (errors.consent) setErrors((prev) => ({ ...prev, consent: undefined }));
                }}
                className={`mt-0.5 w-4 h-4 rounded ${checkboxBg} ${checkboxBorder} accent-hj-red cursor-pointer`}
              />
              <span className={`text-xs md:text-sm ${mutedColor} leading-relaxed font-body`}>
                Ich stimme zu, dass meine E-Mail-Adresse für den Newsletter-Versand verwendet wird. Ich kann mich jederzeit abmelden. Weitere Informationen in der{" "}
                <a href="/datenschutz" className={`${linkColor} underline`} target="_blank" rel="noopener noreferrer">
                  Datenschutzerklärung
                </a>
                .
              </span>
            </label>
            {errors.consent && (
              <p className={`text-xs ${errorColor} mt-1.5 font-body ml-6`}>{errors.consent}</p>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}